import { StateCreator } from "zustand";
import { StoreState } from "..";

export interface HeaderToolsState {
  selectedTool: string;
  selectedFocus: string;
  selectedPersonas: string[];
  selectedOffers: string[];
  selectedPolicies: string[];
}

// Define the header slice of the store
export interface HeaderSlice {
  // State
  headerTools: HeaderToolsState;

  // Actions
  setSelectedTool: (tool: string) => void;
  setSelectedFocus: (focus: string) => void;
  togglePersona: (id: string) => void;
  toggleOffer: (id: string) => void;
  togglePolicy: (id: string) => void;
  resetHeaderTools: () => void;
}

// Initial state for header tools
const initialHeaderToolsState: HeaderToolsState = {
  selectedTool: "",
  selectedFocus: "",
  selectedPersonas: [],
  selectedOffers: [],
  selectedPolicies: [],
};

// Create the header slice
export const createHeaderSlice: StateCreator<
  StoreState,
  [],
  [],
  HeaderSlice
> = (set) => ({
  // Initial state
  headerTools: initialHeaderToolsState,

  // Actions
  setSelectedTool: (tool: string) =>
    set((state) => ({
      headerTools: {
        ...state.headerTools,
        selectedTool: tool,
        // Reset focus when tool changes
        selectedFocus: "",
      },
    })),

  setSelectedFocus: (focus: string) =>
    set((state) => ({
      headerTools: {
        ...state.headerTools,
        selectedFocus: focus,
      },
    })),

  togglePersona: (id: string) =>
    set((state) => {
      const isSelected = state.headerTools.selectedPersonas.includes(id);

      // Backend-mediated write; the persona subscription reflects the result.
      const { userData } = state;
      if (userData?.currentDomainId) {
        const persona = state.personas.find((p) => p.id === id);
        if (persona) {
          state
            .setPersonaEnabled(userData.currentDomainId, id, !isSelected)
            .catch((error) => {
              console.error("Error updating persona enabled state:", error);
            });
        }
      }

      // Don't update local state - let the persona subscription handle it
      return state;
    }),

  toggleOffer: (id: string) =>
    set((state) => {
      const isSelected = state.headerTools.selectedOffers.includes(id);

      // Backend-mediated write; the offer subscription reflects the result.
      const { userData } = state;
      if (userData?.currentDomainId) {
        const offer = state.offers.find((o) => o.id === id);
        if (offer) {
          state
            .setOfferEnabled(userData.currentDomainId, id, !isSelected)
            .catch((error) => {
              console.error("Error updating offer enabled state:", error);
            });
        }
      }

      // Don't update local state - let the offer subscription handle it
      return state;
    }),

  togglePolicy: (id: string) =>
    set((state) => {
      const isSelected = state.headerTools.selectedPolicies.includes(id);

      // Update the is_enabled field in Firestore
      const { currentUser, userData } = state;
      if (currentUser && userData?.currentDomainId) {
        const policy = state.policies.find((p) => p.id === id);
        if (policy) {
          // Use the updatePolicy function from the policy slice
          // The policy subscription will handle updating the header tools state
          state
            .updatePolicy(currentUser.uid, userData.currentDomainId, id, {
              is_enabled: !isSelected,
            })
            .catch((error) => {
              console.error("Error updating policy enabled state:", error);
            });
        }
      }

      // Don't update local state - let the policy subscription handle it
      return state;
    }),

  resetHeaderTools: () =>
    set((state) => {
      // Get the current domain ID
      const currentDomainId = state.userData?.currentDomainId;

      // If no current domain, reset to empty state
      if (!currentDomainId) {
        return {
          headerTools: initialHeaderToolsState,
        };
      }

      // Get personas, offers, and policies from the store
      const { personas, offers, policies } = state;

      // Find enabled personas FOR THE CURRENT DOMAIN
      const enabledPersonas = personas
        .filter(
          (persona) =>
            persona.domain_id === currentDomainId && persona.is_enabled,
        )
        .map((persona) => persona.id);

      // Find enabled offers FOR THE CURRENT DOMAIN
      const enabledOffers = offers
        .filter(
          (offer) => offer.domain_id === currentDomainId && offer.is_enabled,
        )
        .map((offer) => offer.id);

      // Find enabled policies FOR THE CURRENT DOMAIN
      const enabledPolicies = policies
        .filter(
          (policy) => policy.domain_id === currentDomainId && policy.is_enabled,
        )
        .map((policy) => policy.id);

      // Update the header tools with the enabled items from Firestore
      return {
        headerTools: {
          ...initialHeaderToolsState,
          selectedPersonas: enabledPersonas,
          selectedOffers: enabledOffers,
          selectedPolicies: enabledPolicies,
        },
      };
    }),
});
